import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

export interface ExitSlipSkillEarningState {
  // Exit tickets ("ClassExitSlipNode:<n>") that are the last one standing between the student and a
  // skill: every other ticket they were given for it is already passed (see
  // findSkillEarningExitSlipIds). Today's banner promises "CHANCE TO EARN A SKILL!" when today's
  // ticket is one of these.
  //
  // Not narrowed to the unit on screen: the report behind it covers the student's whole target
  // group, so a skill one ticket away is known wherever they are standing. That is the point — the
  // promise is about a skill, and today's ticket can belong to a unit they are not currently looking
  // at.
  //
  // Redux rather than a prop because the two halves of the answer are owned by different components:
  // DashboardSkillExitSlipReportQuery has the report that knows which ticket is the last one, and
  // DashboardExitSlipQuery has today's ticket to match it against. They are siblings under
  // DashboardCards with no common parent below the page.
  exitSlipIds: string[];
}

const initialState: ExitSlipSkillEarningState = { exitSlipIds: [] };

export const exitSlipSkillEarningSlice = createSlice({
  name: 'exitSlipSkillEarning',
  initialState,
  reducers: {
    setSkillEarningExitSlipIds: (state: ExitSlipSkillEarningState, action: PayloadAction<string[]>) => {
      // Same ids, same order: keep the existing array, as exitSlipUnlockedSkillsSlice does. Only one
      // membership test reads this, but a fresh identity on every dispatch would still re-render the
      // banner — which is mid-transition with the result card often enough to matter.
      const unchanged =
        state.exitSlipIds.length === action.payload.length &&
        state.exitSlipIds.every((exitSlipId, index) => exitSlipId === action.payload[index]);
      if (unchanged) {
        return;
      }
      state.exitSlipIds = action.payload;
    },
  },
});

export const selectedSkillEarningExitSlipIds = (state: RootState) => state.exitSlipSkillEarning.exitSlipIds;

export const { setSkillEarningExitSlipIds } = exitSlipSkillEarningSlice.actions;

export default exitSlipSkillEarningSlice.reducer;
